<template>
  <loading v-if="loading" padding="8" />
  <div
    class="grid gap-4"
    v-else
    :class="[id && previewVisible ? 'md:grid-cols-2' : 'grid-cols-1']"
  >
    <div>
      <div
        class="
          -m-4
          mt-0
          mb-8
          p-4
          pb-8
          bg-gray-200
          border-t border-b border-gray-400
          stack
        "
      >
        <div class="grid grid-cols-auto-1 w-full text-left mt-2 mb-4" v-if="id">
          <div>
            <wd-button
              :class="{ 'text-primary-500': formData.featured }"
              transparent
              @input="toggleFeatured"
            >
              <icon name="Star" size="1.5em" />
            </wd-button>
            <wd-button class="ml-2" transparent @input="togglePublished">
              <icon
                :name="formData.published ? 'Eye' : 'NotVisible'"
                size="1.5em"
              />
            </wd-button>
            <wd-button
              class="ml-2"
              transparent
              @input="sendDelete"
              :sending="deleteSending"
            >
              <icon name="Trash" size="1.5em" />
            </wd-button>
          </div>
          <div class="ml-auto" v-if="!previewVisible">
            <wd-button
              class="hidden md:block"
              transparent
              @input="togglePreview"
              >{{ $t("news.manage.preview") }}</wd-button
            >
          </div>
        </div>
        <div class="grid gap-2">
          <input-text
            :value="formData.title"
            label="Titel"
            @update-value="updateForm('title', $event, id || 'NewNewsEntry')"
          />
          <input-text
            :value="formData.slug"
            label="Slug (URL)"
            @update-value="updateForm('slug', $event, id || 'NewNewsEntry')"
          />
        </div>
        <div v-if="id">
          <input-date
            :value="formData.published_at"
            :label="$t('news.manage.publication_date')"
            @update-value="
              updateForm('published_at', $event, id || 'NewNewsEntry')
            "
          />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div class="stack">
            <input-select
              :label="$t('general.category')"
              :options="newsCategories"
              optionlabel="title"
              optionvalue="id"
              :value="formData.news_category_id"
              @update-value="
                updateForm('news_category_id', $event, id || 'NewNewsEntry')
              "
            />
          </div>
          <div>
            <input-select
              :label="$t('activities.activity_category')"
              :options="activityCategories"
              optionlabel="title"
              optionvalue="id"
              :value="formData.activity_category_id"
              @update-value="
                updateForm('activity_category_id', $event, id || 'NewNewsEntry')
              "
            />
          </div>
        </div>
        <div class="mt-2 cursor-pointer">
          <div @click="toggleImageSelect">
            <label v-if="formData.image_id">{{ $t("news.news_image") }}</label>
            <div
              class="cursor-pointer border border-primary-500 p-2"
              v-if="formData.image_id"
            >
              <single-image :image-id="formData.image_id" />
            </div>
            <div class="border border-primary-500 p-2" v-else>
              <icon class="cursor-pointer" name="Image" size="1.5em" />
              <span class="ml-2">{{ $t("news.news_image") }}</span>
            </div>
          </div>
          <div class="my-2">
            <input-textarea
              :value="formData.summary"
              :label="$t('news.summary')"
              @update-value="
                updateForm('summary', $event, id || 'NewNewsEntry')
              "
            />
          </div>
        </div>
        <div class="flex justify-between">
          <div>
            <wd-button
              :disabled="!formData.title || !formData.activity_category_id"
              @input="sendAction"
              :sending="sending"
              >{{ id ? $t("general.update") : $t("general.create") }}</wd-button
            >
          </div>
        </div>
      </div>

      <!-- News Entry Items -->
      <div v-if="id">
        <div
          class="w-full p-4 border border-gray-400 mb-4"
          v-if="newsEntryItems.length < 1 && unsavedItems.length < 1"
        >
          {{ $t("news.manage.no_items_yet") }}
        </div>
        <div v-else>
          <draggable
            v-bind="dragOptions"
            v-model="newsEntryItems"
            @start="drag = true"
            @end="drag = false"
          >
            <transition-group
              class="stack mb-4"
              type="transition"
              :name="!drag ? 'flip-list' : 'no-move'"
            >
              <news-entry-item-form
                v-for="item in newsEntryItems"
                :key="item.id"
                :item="item"
                :preview="!previewVisible"
              />
            </transition-group>
          </draggable>
          <news-entry-item-form
            v-for="(item, index) in unsavedItems"
            :key="index"
            :item="item"
            :preview="!previewVisible"
            :news-entry-id="id"
            @close="removeUnsavedItem(index)"
          />
        </div>
        <div class="mt-3">
          <wd-button
            icon="Plus"
            icon-size="1em"
            icon-stroke="2"
            @input="addUnsaved"
            >{{ $t("news.manage.add_entry_item") }}</wd-button
          >
        </div>
      </div>
    </div>

    <!-- PREVIEW -->
    <div
      class="hidden md:block border border-gray-300"
      v-if="id && previewVisible"
    >
      <div
        class="
          p-1
          px-2
          tracking-wider
          bg-gray-300
          grid grid-cols-1-auto
          uppercase
          text-sm
          font-medium
        "
      >
        <span>{{ $t("news.manage.preview") }}</span>
        <wd-button transparent @input="togglePreview">
          <icon name="Close" />
        </wd-button>
      </div>
      <div class="p-4">
        <h1>{{ formData.title }}</h1>
        <div class="markdown text-xl my-4">
          <vue-markdown
            :source="formData.summary"
            :anchorAttributes="{
              target: '_blank',
              rel: 'nofollow',
            }"
          />
        </div>
        <news-entry-item
          v-for="item in newsEntryItems"
          :key="item.id"
          :item="item"
        />
      </div>
    </div>
    <images
      v-if="imageSelect"
      @close="toggleImageSelect"
      @selection="updateImage"
    />
  </div>
</template>

<script>
import FormMixin from "@/mixins/FormMixin";
import DateMixin from "@/mixins/DateMixin";

import { NewsEntryItem as NewsEntryItemModel } from "@/modules/news/viewmodels/NewsEntryItem";
import _ from "lodash";
import draggable from "vuedraggable";
import VueMarkdown from "vue-markdown";

const NewsEntryItemForm =
  require("@/modules/news/components/NewsEntryItemForm.vue").default;
const NewsEntryItem =
  require("@/modules/news/components/NewsEntryItem.vue").default;
const InputSelect =
  require("@/modules/input/components/InputSelect.vue").default;
const InputTextarea =
  require("@/modules/input/components/InputTextarea.vue").default;
const InputDate = require("@/modules/input/components/InputDate.vue").default;
const Images = require("@/modules/images/components/Images.vue").default;
const SingleImage =
  require("@/modules/images/components/SingleImage.vue").default;

export default {
  name: "NewsEntryManageSite",
  mixins: [FormMixin, DateMixin],
  data() {
    return {
      newsCategories: null,
      deleteSending: false,
      unsavedItems: [],
      previewVisible: false,
      imageSelect: false,
      newsEntry: null,
      drag: false,
    };
  },
  props: {
    id: {
      type: String,
    },
  },
  components: {
    NewsEntryItemForm,
    NewsEntryItem,
    InputSelect,
    InputTextarea,
    InputDate,
    Images,
    SingleImage,
    draggable,
    VueMarkdown,
  },
  methods: {
    sendDelete() {
      this.$store.dispatch("dialog/add", {
        title: "Löschen bestätigen",
        text: `Gesamten Artikel wirklich löschen?`,
        submitFn: () => {
          this.deleteEntry();
        },
      });
    },
    sendAction() {
      let formOptions;
      if (this.id) {
        formOptions = {
          async: true,
          type: "newsentry",
          call: "update",
          id: this.id,
          action: async (response) => {
            this.newsEntry = response;
            this.formData = this.newsEntry;
            if (this.formData.published_at) {
              this.formData.published_at = this.momentFormat(
                this.formData.published_at,
                "YYYY-MM-DD"
              );
            }
            this.$store.dispatch("temp/resetUnsaved");
            this.popup({
              title: this.$t("news.manage.news_entry_updated"),
              autohide: true,
            });
          },
        };
      } else {
        formOptions = {
          async: true,
          type: "newsentry",
          call: "create",
          action: (response) => {
            this.$store.dispatch("temp/resetUnsaved");
            this.goTo("/news-manage/" + response.id);
            this.popup({
              title: this.$t("news.manage.news_entry_created"),
              autohide: true,
            });
          },
        };
      }
      this.submit(formOptions);
    },
    toggleFeatured() {
      let featureFormOptions = {
        async: true,
        type: "newsentry",
        call: "update",
        id: this.id,
        data: { featured: !this.formData.featured },
        action: (response) => {
          this.formData.featured = response.featured;
          this.popup({
            title: this.formData.featured
              ? this.$t("news.manage.news_entry_featured")
              : this.$t("news.manage.news_entry_not_featured"),
            autohide: true,
          });
        },
      };
      this.submit(featureFormOptions);
    },
    togglePublished() {
      let publishFormOptions = {
        async: true,
        type: "newsentry",
        call: "update",
        id: this.id,
        data: { published: !this.formData.published },
        action: (response) => {
          this.newsEntry = response;
          this.formData = response;
          if (this.formData.published_at) {
            this.formData.published_at = this.momentFormat(
              this.formData.published_at,
              "YYYY-MM-DD"
            );
          }
          this.popup({
            title: this.formData.published
              ? this.$t("news.manage.news_entry_published")
              : this.$t("news.manage.news_entry_unpublished"),
            autohide: true,
          });
        },
      };
      this.submit(publishFormOptions);
    },
    deleteEntry() {
      let deleteFormOptions = {
        async: true,
        type: "newsentry",
        call: "destroy",
        sendingVar: false,
        id: this.id,
        action: (response) => {
          this.deleteSending = false;
          this.goTo("/news");
          this.popup({
            title: this.$t("news.manage.news_entry_deleted"),
          });
        },
      };
      this.submit(deleteFormOptions);
    },
    addUnsaved() {
      let unsavedItem = new NewsEntryItemModel({});
      this.unsavedItems.push(unsavedItem);
      this.$store.dispatch("temp/setUnsavedItem", {item: unsavedItem.tempId, key: 'new'});
    },
    removeUnsavedItem(Idx) {
      let unsavedItem = this.unsavedItems.splice(Idx, 1)[0];
      this.$store.dispatch("temp/removeUnsavedItem", {item: unsavedItem.tempId, key: 'new'});
    },
    togglePreview() {
      this.previewVisible = !this.previewVisible;
    },
    toggleImageSelect() {
      this.imageSelect = !this.imageSelect;
    },
    updateImage(Image) {
      this.toggleImageSelect();
      this.updateForm("image_id", Image);
      this.$store.dispatch("temp/setUnsavedItem", "image");
    },
    reorderItems(Items) {
      this.$store.dispatch("newsentryitem/reorderItems", Items);

      let reorderFormOptions = {
        async: true,
        custom: true,
        type: "newsentryitem",
        call: "ReorderItems",
        setSending: false,
        data: { id: this.id, orderArr: Items.map((e) => e.id) },
        action: (response) => {},
      };

      this.submit(reorderFormOptions);
    },
  },
  computed: {
    newsEntryItems: {
      get() {
        return this.$store.getters["newsentryitem/itemsOfEntry"](this.id);
      },
      set(Items) {
        this.reorderItems(Items);
      },
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
        handle: ".drag-handle",
      };
    },
  },
  async created() {
    if (this.id) {
      this.newsEntry = await this.$store.getters["newsentry/api"].Show(this.id);
      this.formData = Object.assign({}, this.newsEntry);
      if (this.formData.published_at) {
        this.formData.published_at = this.momentFormat(
          this.formData.published_at,
          "YYYY-MM-DD"
        );
      }

      await this.$store.getters["newsentryitem/api"].Custom.ShowItemsOfEntry(
        this.id
      );
    }

    this.newsCategories = await this.$store.getters[
      "newscategory/api"
    ].GetAll();

    this.activityCategories = await this.$store.getters[
      "activitycategory/api"
    ].GetAll();

    if (window.innerWidth > 1000) this.previewVisible = true;

    this.loaded();
  },
};
</script>

<style lang="css" scoped>
.ghost {
  @apply opacity-50 bg-gray-200;
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
</style>
